
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

const maxTransferLogOptions = [
  0,
  1,
  2,
  3,
  5,
  10,
  15,
  20,
  25,
  50,
];

export default function AdminUnHappyPayersReport() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [timeRange, setTimeRange] = useState({
    start: "",
    end: "",
  });

  const [maxTransferLogs, setMaxTransferLogs] =
    useState("0");

  const [searchFilter, setSearchFilter] =
    useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleRangeChange = useCallback(
    ({ start, end }) => {
      setTimeRange({
        start,
        end,
      });
    },
    []
  );

  const fetchReport = useCallback(async () => {
    if (
      !timeRange.start ||
      !timeRange.end ||
      maxTransferLogs === ""
    ) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${localStorage.getItem("token")}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/payers-who-are-not-happy`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
            max_transfer_logs:
              Number(maxTransferLogs),
          },
        }
      );

      setRows(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Error fetching unhappy payers report:",
        error
      );

      setRows([]);

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "admin_unhappy_payers_report.messages.fetch_failed"
            )
          )
      );
    } finally {
      setLoading(false);
    }
  }, [
    maxTransferLogs,
    timeRange.end,
    timeRange.start,
    t,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    const search =
      searchFilter.trim().toLowerCase();

    if (!search) {
      setFilteredRows(rows);
      return;
    }

    const filtered = rows.filter((row) => {
      const searchableValues = [
        row.user_id,
        row.userno,
        row.fullname,
        row.email,
        row.user_tax_id,
        row.sponsor,
        row.sponsor_no,
        row.wallet_count,
        row.total_ticket_balance,
        row.total_money_balance,
        row.transfer_log_count,
        row.profile_notes,
        row.last_activity_in_period,
        row.last_activity_ever,
      ];

      return searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search)
      );
    });

    setFilteredRows(filtered);
  }, [rows, searchFilter]);

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    return new Date(value)
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  return (
    <StandardScreen
      topNav={
        <TopNav
          title={t(
            "admin_unhappy_payers_report.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card
              title={t(
                "admin_unhappy_payers_report.title"
              )}
            >
              <Stack gap="3">
                <FormField>
                  <TimeRangeSelector
                    onRangeChange={
                      handleRangeChange
                    }
                  />
                </FormField>

                <FormField
                  label={t(
                    "admin_unhappy_payers_report.fields.max_transfer_logs.label"
                  )}
                >
                  <SelectInput
                    value={maxTransferLogs}
                    onChange={(event) =>
                      setMaxTransferLogs(
                        event.target.value
                      )
                    }
                  >
                    {maxTransferLogOptions.map(
                      (count) => (
                        <option
                          key={count}
                          value={count}
                        >
                          {t(
                            "admin_unhappy_payers_report.fields.max_transfer_logs.option",
                            {
                              count,
                            }
                          )}
                        </option>
                      )
                    )}
                  </SelectInput>
                </FormField>

                <FormField
                  label={t(
                    "admin_unhappy_payers_report.fields.search.label"
                  )}
                >
                  <TextInput
                    type="text"
                    value={searchFilter}
                    onChange={(event) =>
                      setSearchFilter(
                        event.target.value
                      )
                    }
                    placeholder={t(
                      "admin_unhappy_payers_report.fields.search.placeholder"
                    )}
                  />
                </FormField>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            <Card>
              <TableShell maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>
                      <Th
                        columnIndex={0}
                        sticky
                      >
                        {t(
                          "admin_unhappy_payers_report.table.fullname"
                        )}
                      </Th>

                      <Th
                        columnIndex={1}
                        sticky
                      >
                        {t(
                          "admin_unhappy_payers_report.table.userno"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.sponsor"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.sponsor_no"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.wallet_count"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.total_ticket_balance"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.total_money_balance"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.transfer_log_count"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.last_activity_in_period"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.last_activity_ever"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.email"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_unhappy_payers_report.table.user_tax_id"
                        )}
                      </Th>
                    </tr>
                  </thead>

                  <tbody>
                    {!loading &&
                      filteredRows.map((row) => (
                        <tr key={row.user_id}>
                          <Td
                            columnIndex={0}
                            sticky
                          >
                            {row.fullname || "-"}
                          </Td>

                          <Td
                            columnIndex={1}
                            sticky
                          >
                            {row.userno || "-"}
                          </Td>

                          <Td>
                            {row.sponsor || "-"}
                          </Td>

                          <Td>
                            {row.sponsor_no || "-"}
                          </Td>

                          <NumberTd>
                            {row.wallet_count ?? 0}
                          </NumberTd>

                          <NumberTd>
                            {row.total_ticket_balance ??
                              0}
                          </NumberTd>

                          <NumberTd>
                            {Number(
                              row.total_money_balance ??
                                0
                            ).toFixed(2)}
                          </NumberTd>

                          <NumberTd>
                            {row.transfer_log_count ??
                              0}
                          </NumberTd>

                          <Td>
                            {formatDateTime(
                              row.last_activity_in_period
                            )}
                          </Td>

                          <Td>
                            {formatDateTime(
                              row.last_activity_ever
                            )}
                          </Td>

                          <Td>
                            {row.email || "-"}
                          </Td>

                          <Td>
                            {row.user_tax_id || "-"}
                          </Td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </TableShell>

              {loading && (
                <div
                  style={{
                    padding: "1rem",
                    textAlign: "center",
                  }}
                >
                  {t(
                    "admin_unhappy_payers_report.messages.loading"
                  )}
                </div>
              )}

              {!loading &&
                filteredRows.length === 0 && (
                  <div
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                    }}
                  >
                    {t(
                      "admin_unhappy_payers_report.messages.empty"
                    )}
                  </div>
                )}
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}
